import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { SimpleSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { SEMRESATTRS_SERVICE_NAME } from '@opentelemetry/semantic-conventions';
import { trace, context, propagation } from '@opentelemetry/api';

// Minimal fetch-based OTLP/JSON exporter — avoids version mismatches
// with @opentelemetry/exporter-trace-otlp-http bundling its own sdk-trace-base.
class HoneycombExporter {
  private apiKey: string;
  constructor(apiKey: string) { this.apiKey = apiKey; }

  export(spans: any[], resultCallback: (result: { code: number }) => void): void {
    const body = JSON.stringify({
      resourceSpans: spans.map((span: any) => ({
        resource: { attributes: [] },
        scopeSpans: [{
          scope: { name: 'personal-site' },
          spans: [{
            traceId: span.spanContext().traceId,
            spanId: span.spanContext().spanId,
            parentSpanId: span.parentSpanId || '',
            name: span.name,
            kind: span.kind,
            startTimeUnixNano: String(span.startTime[0] * 1e9 + span.startTime[1]),
            endTimeUnixNano: String(span.endTime[0] * 1e9 + span.endTime[1]),
            attributes: Object.entries(span.attributes || {}).map(([k, v]) => ({
              key: k, value: toAnyValue(v),
            })),
            status: { code: span.status?.code ?? 0 },
            events: [],
            links: [],
          }],
        }],
      })),
    });

    fetch('https://api.honeycomb.io/v1/traces', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-honeycomb-team': this.apiKey,
        'x-honeycomb-dataset': 'personal-site',
      },
      body,
    })
      .then(() => resultCallback({ code: 0 }))
      .catch(() => resultCallback({ code: 1 }));
  }

  shutdown(): Promise<void> { return Promise.resolve(); }
}

function toAnyValue(v: unknown): object {
  if (typeof v === 'string') return { stringValue: v };
  if (typeof v === 'number') return Number.isInteger(v) ? { intValue: v } : { doubleValue: v };
  if (typeof v === 'boolean') return { boolValue: v };
  return { stringValue: String(v) };
}

let initialised = false;

export function initBrowserTelemetry() {
  if (initialised) return;
  const apiKey = process.env.REACT_APP_HONEYCOMB_API_KEY;
  if (!apiKey) return;

  const provider = new WebTracerProvider({
    resource: resourceFromAttributes({
      [SEMRESATTRS_SERVICE_NAME]: 'personal-site',
    }),
    spanProcessors: [new SimpleSpanProcessor(new HoneycombExporter(apiKey))],
  });

  provider.register({ contextManager: new ZoneContextManager() });
  initialised = true;
}

export function getTracer() {
  return trace.getTracer('personal-site');
}

export function recordEvent(name: string, attributes: Record<string, string | number | boolean>) {
  const span = getTracer().startSpan(name);
  span.setAttributes(attributes);
  span.end();
}

export function getTraceHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  propagation.inject(context.active(), headers);
  return headers;
}
